import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskApi, userApi, departmentApi, settingsApiFull } from '../api';
import { useAuth } from '../context/AuthContext';
import { Btn, Alert, Spinner, EmptyState, PriorityBadge } from '../components/ui';

const PRIORITIES = ['ALL', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

// ── Pool task card ──────────────────────────────────────────────────────────────
function PoolCard({ task, onClaim, onAssign, onView, isAdminOrAbove }) {
  const today     = new Date().toISOString().split('T')[0];
  const isOverdue = task.deadline && task.deadline < today;
  const isDueToday = task.deadline === today;

  return (
    <div style={{
      background: 'var(--color-surface)', borderRadius: 'var(--radius-md)',
      border: `1px solid ${isOverdue ? '#FEB2B2' : 'var(--color-border)'}`,
      padding: '16px',
      borderLeft: `4px solid ${isOverdue ? 'var(--color-danger)' : isDueToday ? 'var(--color-warning)' : task.priority === 'CRITICAL' ? '#C53030' : 'var(--color-border)'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontWeight: 600, fontSize: '15px', marginBottom: '6px', cursor: 'pointer', color: 'var(--color-text)' }}
            onClick={onView}>
            {task.title}
          </p>
          {task.description && (
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '8px', lineHeight: 1.4,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {task.description}
            </p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
            <PriorityBadge priority={task.priority} />
            {task.department && (
              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', background: 'var(--color-bg)', padding: '2px 8px', borderRadius: '999px' }}>
                {task.department.name}
              </span>
            )}
            {task.tags?.map(t => (
              <span key={t} style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: 'var(--color-primary-lighter)', color: 'var(--color-primary)' }}>{t}</span>
            ))}
          </div>
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {isOverdue ? (
            <p style={{ fontSize: '12px', color: 'var(--color-danger)', fontWeight: 600 }}>
              {task.daysOverdue}d overdue
            </p>
          ) : isDueToday ? (
            <p style={{ fontSize: '12px', color: 'var(--color-warning)', fontWeight: 600 }}>Due today</p>
          ) : (
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>Due {task.deadline}</p>
          )}
          <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
            Added by {task.assignedBy?.name}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
        <Btn size="sm" onClick={onClaim}>Claim Task</Btn>
        {isAdminOrAbove && (
          <Btn size="sm" variant="secondary" onClick={onAssign}>Assign to Employee</Btn>
        )}
        <Btn size="sm" variant="ghost" onClick={onView}>View Details</Btn>
      </div>
    </div>
  );
}

// ── Add to Pool modal ───────────────────────────────────────────────────────────
function AddToPoolModal({ departments, tags, onClose, onAdded }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: '', description: '', priority: 'MEDIUM',
    deadline: '', departmentId: '', tags: [],
  });
  const [error,  setError]  = useState('');
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleTag = (t) => setForm(f => ({
    ...f, tags: f.tags.includes(t) ? f.tags.filter(x => x !== t) : [...f.tags, t],
  }));

  const minDate = new Date(Date.now() + 86400000).toISOString().split('T')[0];

  const submit = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.deadline)     { setError('Deadline is required'); return; }
    setError(''); setSaving(true);
    try {
      const res = await taskApi.create({
        title:        form.title,
        description:  form.description,
        priority:     form.priority,
        deadline:     form.deadline,
        departmentId: form.departmentId || null,
        tags:         form.tags,
        poolTask:     true,
      });
      if (res.data.success) { onAdded(res.data.data); onClose(); }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to add task to pool');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={M.overlay}>
      <div style={M.modal}>
        <div style={M.header}>
          <h3 style={M.title}>Add Task to Pool</h3>
          <button onClick={onClose} style={M.closeBtn}>×</button>
        </div>

        {error && <Alert type="error" message={error} />}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
          <Field label="Title *">
            <input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="What needs to be done?" style={M.input} />
          </Field>

          <Field label="Description">
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Additional details (optional)" rows={2}
              style={{ ...M.input, resize: 'vertical' }} />
          </Field>

          <Field label="Department">
            <select value={form.departmentId} onChange={e => set('departmentId', e.target.value)} style={M.input}>
              <option value="">— No department —</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>

          <Field label="Priority">
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
              {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(p => (
                <button key={p} onClick={() => set('priority', p)} style={{
                  padding: '6px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  fontSize: '12px', fontWeight: 500,
                  background: form.priority === p ? 'var(--color-primary)' : 'var(--color-surface)',
                  color:      form.priority === p ? '#fff' : 'var(--color-text-secondary)',
                  border:     `1.5px solid ${form.priority === p ? 'var(--color-primary)' : 'var(--color-border)'}`,
                }}>{p}</button>
              ))}
            </div>
          </Field>

          <Field label="Deadline *">
            <input type="date" min={minDate} value={form.deadline}
              onChange={e => set('deadline', e.target.value)} style={M.input} />
          </Field>

          {tags.length > 0 && (
            <Field label="Tags">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                {tags.map(t => (
                  <button key={t} onClick={() => toggleTag(t)} style={{
                    padding: '4px 10px', borderRadius: '999px', cursor: 'pointer', fontSize: '12px',
                    background: form.tags.includes(t) ? 'var(--color-primary)' : 'var(--color-surface)',
                    color:      form.tags.includes(t) ? '#fff' : 'var(--color-text-secondary)',
                    border:     `1px solid ${form.tags.includes(t) ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  }}>{t}</button>
                ))}
              </div>
            </Field>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={submit} loading={saving}>Add to Pool</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Assign modal ────────────────────────────────────────────────────────────────
function AssignModal({ task, employees, onClose, onAssigned }) {
  const [selectedId, setSelected] = useState('');
  const [error,      setError]    = useState('');
  const [saving,     setSaving]   = useState(false);

  const selectedEmp = employees.find(e => e.id === selectedId);
  const isAway      = selectedEmp?.away;

  const submit = async () => {
    if (!selectedId) { setError('Please select an employee'); return; }
    setError(''); setSaving(true);
    try {
      const res = await taskApi.update(task.id, { assignedToId: selectedId });
      if (res.data.success) { onAssigned(res.data.data); onClose(); }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to assign task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={M.overlay}>
      <div style={{ ...M.modal, maxWidth: '400px' }}>
        <div style={M.header}>
          <h3 style={M.title}>Assign Task</h3>
          <button onClick={onClose} style={M.closeBtn}>×</button>
        </div>

        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginBottom: '16px' }}>
          Assigning: <strong>{task.title}</strong>
        </p>

        {error && <Alert type="error" message={error} />}

        {isAway && (
          <div style={{ padding: '10px 14px', background: '#FFFBEB', border: '1px solid #FAF089', borderRadius: 'var(--radius-md)', marginBottom: '12px', fontSize: '13px', color: '#744210' }}>
            ⚠️ This employee is currently Away{selectedEmp.awayReturnDate ? ` — returns ${selectedEmp.awayReturnDate}` : ''}. You can still assign but they may not see it until they return.
          </div>
        )}

        <select value={selectedId} onChange={e => setSelected(e.target.value)} style={{ ...M.input, marginBottom: '16px' }}>
          <option value="">— Select employee —</option>
          {employees.filter(e => e.active).map(e => (
            <option key={e.id} value={e.id}>
              {e.name} ({e.employeeId}){e.away ? ' — Away' : ''}
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} loading={saving}>Assign</Btn>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '6px' }}>{label}</label>
      {children}
    </div>
  );
}

// ── Main Pool page ──────────────────────────────────────────────────────────────
export default function Pool() {
  const { user }                    = useAuth();
  const navigate                    = useNavigate();
  const [tasks,       setTasks]     = useState([]);
  const [employees,   setEmployees] = useState([]);
  const [departments, setDepts]     = useState([]);
  const [tags,        setTags]      = useState([]);
  const [loading,     setLoading]   = useState(true);
  const [error,       setError]     = useState('');
  const [showAdd,     setShowAdd]   = useState(false);
  const [assignTask,  setAssignTask]= useState(null);

  // filters
  const [search,   setSearch]   = useState('');
  const [priority, setPriority] = useState('ALL');
  const [deptFilter, setDeptFilter] = useState('ALL');

  const isAdminOrAbove = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

  const load = useCallback(() => {
    const calls = [taskApi.getPool()];
    if (isAdminOrAbove) {
      calls.push(userApi.getAll());
      calls.push(departmentApi.getAll());
    }
    calls.push(settingsApiFull.get());

    Promise.all(calls)
      .then(results => {
        setTasks(results[0].data.data || []);
        if (isAdminOrAbove) {
          setEmployees(results[1].data.data || []);
          setDepts(results[2].data.data || []);
          setTags(results[3]?.data?.data?.masterTags || []);
        } else {
          setTags(results[1]?.data?.data?.masterTags || []);
        }
      })
      .catch(() => setError('Failed to load pool'))
      .finally(() => setLoading(false));
  }, [isAdminOrAbove]);

  useEffect(() => { load(); }, [load]);

  const handleClaim = async (task) => {
    setError('');
    try {
      await taskApi.claim(task.id);
      navigate(`/tasks/${task.id}`);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to claim task');
    }
  };

  const filtered = tasks.filter(t => {
    const matchSearch   = !search     || t.title?.toLowerCase().includes(search.toLowerCase());
    const matchPriority = priority === 'ALL' || t.priority === priority;
    const matchDept     = deptFilter === 'ALL' || t.department?.id === deptFilter;
    return matchSearch && matchPriority && matchDept;
  });

  // Sort: overdue first, then by deadline
  const sorted = [...filtered].sort((a, b) => {
    const today = new Date().toISOString().split('T')[0];
    const aOver = a.deadline < today;
    const bOver = b.deadline < today;
    if (aOver && !bOver) return -1;
    if (!aOver && bOver) return 1;
    return new Date(a.deadline) - new Date(b.deadline);
  });

  const overdueCount = tasks.filter(t => {
    const today = new Date().toISOString().split('T')[0];
    return t.deadline && t.deadline < today;
  }).length;

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h1 style={S.title}>Task Pool</h1>
          <p style={S.sub}>
            {tasks.length} unclaimed task{tasks.length !== 1 ? 's' : ''}
            {overdueCount > 0 && (
              <span style={{ color: 'var(--color-danger)', marginLeft: '8px', fontWeight: 600 }}>
                · {overdueCount} overdue
              </span>
            )}
          </p>
        </div>
        <Btn onClick={() => setShowAdd(true)}>+ Add to Pool</Btn>
      </div>

      {error && <Alert type="error" message={error} />}

      {/* Info banner */}
      <div style={{ padding: '12px 16px', background: 'var(--color-primary-lighter)', borderRadius: 'var(--radius-md)', marginBottom: '16px', fontSize: '13px', color: 'var(--color-primary)' }}>
        These tasks are unassigned. Claim one to take ownership, or an Admin can assign it directly to an employee.
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        <input
          placeholder="Search pool tasks..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={S.input}
        />
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {PRIORITIES.map(p => (
            <FilterChip key={p} active={priority === p} onClick={() => setPriority(p)}>
              {p === 'ALL' ? 'All Priority' : p}
            </FilterChip>
          ))}
        </div>
        {isAdminOrAbove && departments.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <FilterChip active={deptFilter === 'ALL'} onClick={() => setDeptFilter('ALL')}>All Depts</FilterChip>
            {departments.map(d => (
              <FilterChip key={d.id} active={deptFilter === d.id} onClick={() => setDeptFilter(d.id)}>
                {d.name}
              </FilterChip>
            ))}
          </div>
        )}
      </div>

      {/* Task list */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
      ) : sorted.length === 0 ? (
        <EmptyState
          icon="⊕"
          title={search || priority !== 'ALL' ? 'No tasks match your filters' : 'Pool is empty'}
          subtitle={!search && priority === 'ALL' ? 'Add a task to the pool and anyone can claim it.' : ''}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {sorted.map(task => (
            <PoolCard
              key={task.id}
              task={task}
              isAdminOrAbove={isAdminOrAbove}
              onClaim={() => handleClaim(task)}
              onAssign={() => setAssignTask(task)}
              onView={() => navigate(`/tasks/${task.id}`)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showAdd && (
        <AddToPoolModal
          departments={departments}
          tags={tags}
          onClose={() => setShowAdd(false)}
          onAdded={(t) => { setTasks(prev => [t, ...prev]); }}
        />
      )}

      {assignTask && (
        <AssignModal
          task={assignTask}
          employees={employees}
          onClose={() => setAssignTask(null)}
          onAssigned={() => { setAssignTask(null); load(); }}
        />
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '5px 12px', borderRadius: '999px', cursor: 'pointer',
      fontSize: '12px', fontWeight: active ? 600 : 400,
      background: active ? 'var(--color-primary)' : 'var(--color-surface)',
      color:      active ? '#fff' : 'var(--color-text-secondary)',
      border:     `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
    }}>{children}</button>
  );
}

const S = {
  title: { fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '2px' },
  sub:   { fontSize: '13px', color: 'var(--color-text-muted)' },
  input: { padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', width: '100%' },
};

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '16px' },
  modal:   { background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  title:   { fontSize: '18px', fontWeight: 600, color: 'var(--color-primary)' },
  closeBtn:{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--color-text-muted)' },
  input:   { width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', color: 'var(--color-text)', background: 'var(--color-surface)' },
};
